import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { APP_LOCALES, isAppLocale, localeMeta, type AppLocale } from '@redevicer/shared'

/** ISO 3166-1 alpha-2 for flag images (locale code `uk` → Ukraine `ua`). */
const FLAG_COUNTRY: Record<AppLocale, string> = {
  en: 'gb',
  lt: 'lt',
  de: 'de',
  fr: 'fr',
  es: 'es',
  pl: 'pl',
  ru: 'ru',
  pt: 'pt',
  it: 'it',
  nl: 'nl',
  uk: 'ua',
}

function FlagIcon({ code }: { code: AppLocale }) {
  const cc = FLAG_COUNTRY[code]
  return (
    <img
      className="lang-picker__flag-img"
      src={`https://flagcdn.com/w40/${cc}.png`}
      srcSet={`https://flagcdn.com/w80/${cc}.png 2x`}
      alt=""
      width={20}
      height={15}
      loading="lazy"
      decoding="async"
    />
  )
}

type Props = {
  value: string
  onChange: (code: string) => void
  className?: string
  id?: string
  /** Header: flag icon only — no text label */
  variant?: 'full' | 'icon'
}

export default function LanguageSelect({ value, onChange, className = '', id, variant = 'full' }: Props) {
  const { t } = useTranslation()
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const current = isAppLocale(value) ? value : 'en'
  const currentMeta = localeMeta(current)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function pick(code: AppLocale) {
    onChange(code)
    setOpen(false)
  }

  const iconOnly = variant === 'icon'

  return (
    <div
      ref={rootRef}
      id={id}
      className={`lang-picker ${iconOnly ? 'lang-picker--icon' : ''} ${className}`.trim()}
    >
      <button
        type="button"
        className="lang-picker__trigger"
        aria-label={t('menu.language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
      >
        <FlagIcon code={current} />
        {!iconOnly ? (
          <>
            <span className="lang-picker__label">{currentMeta.nativeLabel}</span>
            <span className="lang-picker__chevron" aria-hidden>
              ▾
            </span>
          </>
        ) : null}
      </button>
      {open ? (
        <ul id={listId} className={`lang-picker__menu${iconOnly ? ' lang-picker__menu--icon' : ''}`} role="listbox" aria-label={t('menu.language')}>
          {APP_LOCALES.map((loc) => {
            const selected = loc.code === current
            return (
              <li key={loc.code} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-label={loc.nativeLabel}
                  className={`lang-picker__option${selected ? ' lang-picker__option--active' : ''}${iconOnly ? ' lang-picker__option--icon' : ''}`}
                  onClick={() => pick(loc.code)}
                >
                  <FlagIcon code={loc.code} />
                  {!iconOnly ? <span>{loc.nativeLabel}</span> : null}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
